<template>
  <div id="page-product-form">
    <vs-row>
      <vs-col>
        <vs-row>
          <vs-col>
            <div class="page-title mb-4" >
              <h2>{{ cardTitleLocal }}</h2>
            </div>

            <card-page card-title="Report">
              <vue-element-loading :active="busy"></vue-element-loading>
              <vue-form-generator
                  ref="form"
                  :model="model"
                  :options="formOptions"
                  :schema="schema"
                  :class="readonly ? 'readonly-form py-4' : 'py-4'"
              />
            </card-page>

            <card-page>
              <grid-panel
                  ref="recipients"
                  :auto-size="true"
                  :column-defs="columnDefs"
                  :data="recipients"
                  height="short"
                  :loading="gridLoading"
                  title="Members"
                  @update:gridApi="(gridApi) => this.gridApi = gridApi"
              >
                <vs-dropdown-item v-if="!readonly">
                  <span
                      class="flex items-center"
                      @click="removeRecipients"
                  >
                    <feather-icon icon="MinusIcon" svgClasses="h-4 w-4" class="mr-2" />
                    <span>Remove Recipients</span>
                  </span>
                </vs-dropdown-item>
              </grid-panel>
            </card-page>

            <card-page card-title="Add Member" v-if="!readonly && !hideAddMember">
              <div class="flex">
                <vue-form-generator
                    class="w-1/4 py-4"
                    ref="type-form"
                    :model="recipientsModel"
                    :options="formOptions"
                    :schema="typeSchema"
                />
                <vue-form-generator
                    v-show="this.emailTypeState === 'Contact'"
                    class="w-3/4 py-4"
                    ref="contact-form"
                    :model="recipientsModel"
                    :options="formOptions"
                    :schema="contactEmailSchema"
                />
                <vue-form-generator
                    v-show="this.emailTypeState === 'Entity'"
                    class="w-3/4 py-4"
                    ref="company-form"
                    :model="recipientsModel"
                    :options="formOptions"
                    :schema="companyEmailSchema"
                />
              </div>
            </card-page>
          </vs-col>
        </vs-row>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from '@/components/mixins/FormModelMixin';
import {selectOptions} from '@/components/form/FormElementOption';
import GridPanel from '@/views/components/ag-grid/GridPanel';
import NotificationMixin from '@/components/mixins/NotificationMixin';
import CardPage from '../../../components/Views/CardPage';
import FormFieldValidationMixin
  from '../../../../components/mixins/FormFieldValidationMixin';

const DistributionTypeId = 57;

export default {
  name: 'DistributionListForm',
  components: {
    GridPanel,
    CardPage,
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    disallowedReportTypes: {
      type: Array,
      required: false,
      default: () => [],
    },
    hideAddMember: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  mixins: [FormModelMixin, NotificationMixin, FormFieldValidationMixin],
  data() {
    return {
      gridApi: null,
      model: {
        FacilityReportType_FK: null,
        Company_FK: {},
      },
      recipientsModel: {
        company: {},
        contact: {},
        emailType: null,
      },
      emailTypeState: null,
      schema: {
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'vx-col w-1/4 pb-0 mb-0',
                model: 'FacilityReportType_FK',
                values: () => this.reportTypes,
                selectOptions,
                required: true,
                validator: 'required',
              },
            ]
          },
        ],
      },
      typeSchema:{
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-full',
                label: 'Type',
                model: 'emailType',
                values: [
                  {id: 0, name: 'Entity'},
                  {id: 1, name: 'Contact'},
                ],
                onChanged: (model) => {
                  this.setDefaultCompany(model.emailType.id);
                },
                selectOptions: {
                  ...selectOptions,
                  loading: false,
                },
              },
            ]
          },
        ]
      },

      companyEmailSchema: {
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3 mb-0',
                label: 'Entity Name',
                model: 'company.Company_FK',
                values: () => this.companies,
                selectOptions,
                validator: async (value, field, model) => {
                  const company = model.company
                  this.companyEmailSchema.groups[0].fields[1].selectOptions.loading = true;

                  this.companyEmailAddresses = [];
                  delete this.recipientsModel.company.Email_FK;

                  await this.loadOption(
                      () => this.$api.companies.relationship('emailAddresses', company.Company_FK.id),
                      'companyEmailAddresses',
                      'EmailAddress'
                  );

                  this.companyEmailSchema.groups[0].fields[1].selectOptions.loading = false;
                },
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3 mb-0',
                label: 'Entity Email Address',
                model: 'company.Email_FK',
                values: () => this.companyEmailAddresses.filter(({id}) => !this.usedRecipientIds.includes(id)),
                selectOptions: {
                  ...selectOptions,
                  loading: false,
                },
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: ({company}) => this.gridLoading || !company.Email_FK,
                type: 'button',
                styleClasses: 'w-full flex justify-end',
                buttonText: 'Add',
                onClick: async ({company}, schema) => {
                  await this.addRecipient(company);
                }
              },
            ]
          }
        ]
      },
      contactEmailSchema: {
        groups: [
          {
            styleClasses: 'w-full flex flex-wrap justify-end',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3 mb-0',
                label: 'Entity Name',
                model: 'contact.Company_FK',
                values: () => this.companies,
                selectOptions,
                validator: async (value, field, model) => {
                  const contact = model.contact;
                  const options = this.contactEmailSchema.groups[0].fields[1].selectOptions;
                  options.loading = true;

                  this.contacts = [];
                  delete this.recipientsModel.contact.Contact_FK;

                  await this.loadOption(
                      () => this.$api.companies.relationship('contacts', contact.Company_FK.id, { orderBy: [ 'LastName', 'FirstName' ] }),
                      'contacts',
                      'Name'
                      // ({FirstName, LastName}) => FirstName + ' ' + LastName,
                      // ['FirstName', 'LastName']
                  );

                  options.loading = false;
                },
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3 mb-0',
                label: 'Contact Name',
                model: 'contact.Contact_FK',
                values: () => this.contacts,
                selectOptions: {
                  ...selectOptions,
                  loading: false,
                },
                onChanged: async ({contact}) => {
                  const options = this.contactEmailSchema.groups[0].fields[2].selectOptions;
                  options.loading = true;

                  this.contactEmailAddresses = [];
                  delete this.recipientsModel.contact.Email_FK;

                  await this.loadOption(
                      () => this.$api.users.relationship('emailAddresses', contact.Contact_FK.id, {EmailType_FK: DistributionTypeId, orderBy: 'EmailAddress'}),
                      'contactEmailAddresses',
                      'EmailAddress'
                  );

                  options.loading = false;
                },
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3 mb-0',
                label: 'Contact Email Address',
                model: 'contact.Email_FK',
                values: () => this.contactEmailAddresses
                    .filter(({id}) => !this.usedContactRecipientIds.includes(id)),
                selectOptions: {
                  ...selectOptions,
                  loading: false,
                },
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                disabled: ({contact}) => this.gridLoading || !contact.Email_FK || this.addingRecipient,
                type: 'button',
                styleClasses: 'w-full flex justify-end',
                buttonText: 'Add',
                onClick: async ({contact}, schema) => {
                  await this.addRecipient(contact);
                }
              },
            ]
          }
        ],
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      url: null,
      columnDefs: [
        {
          checkboxSelection: !this.readonly,
          headerName: 'Email',
          field: 'EmailAddress',
          width: 350,
          valueGetter: ({data}) => {
            const {company_email, contact_email, Contact_FK} = data;

            if (company_email && !Contact_FK) {
              return company_email.EmailAddress;
            }

            if (contact_email) {
              return contact_email.EmailAddress;
            }

            return '';
          }
        },
        {
          headerName: 'Name',
          field: 'FirstName',
          width: 350,
          valueGetter: ({data}) => {
            const {company, contact} = data;

            if (contact) {
              return contact.FirstName + ' ' + contact.LastName;
            }

            return company.CompanyName;
          },
        },
      ],
      reportTypes: [],
      companies: [],
      contacts: [],
      companyEmailAddresses: [],
      contactEmailAddresses: [],
      options: [
        {
          endpoint: () => this.$api.facilities.distributionLists.types.list(),
          optionsFieldName: 'reportTypes',
          nameFieldName: 'Type',
          idFieldName: 'FacilityReportType_FK',
        },
        {
          endpoint: () => this.$api.companies.list(),
          optionsFieldName: 'companies',
          nameFieldName: 'CompanyName',
          idFieldName: 'Company_FK',
        },
      ],
      recipients: [],
      gridLoading: false,
      reportId: null,
      addingRecipient: false,
    };
  },
  computed: {
    cardTitleLocal() {
      if (this.cardTitle) {
        return this.cardTitle;
      }

      if (this.model.id) {
        return `FDO #${this.model.FDO_FK}`;
      }

      return '';
    },
    usedRecipientIds() {
      return this.recipients.map(({Email_FK}) => parseInt(Email_FK));
    },
    usedContactRecipientIds() {
      return this.recipients.filter(({contact_email}) => contact_email)
          .map(({Email_FK}) => parseInt(Email_FK));
    },
  },
  watch: {
    'recipientsModel.emailType'(newValue, oldValue) {
      if (newValue) {
        this.emailTypeState = newValue.name;
      }
    },
    'model.id'(newValue, oldValue) {
      if (newValue) {
        this.reportId = parseInt(this.model.FacilityReportType_FK);
        this.refreshRecipients();
      }
    },
  },
  methods: {
    async setDefaultCompany(type) {
      const { data } = await this.$api.users.getEntity();
      const companyId = data.data.Company_PK;

      const company = this.companies.find(({id}) => Number(id) === companyId);
      this.$set(this.recipientsModel.company, 'Company_FK', company);
      this.$set(this.recipientsModel.contact, 'Company_FK', company);

      if (type === 0) {
        this.validateField('company.Company_FK', 'company-form');
      }

      if (type === 1) {
        this.validateField('contact.Company_FK', 'contact-form');
      }
    },
    async refreshRecipients() {
      this.gridLoading = true;
      try {
        const {data} = await this.$api.facilities.distributionLists.recipients(this.model.id);
        this.recipients = data.data;
      } catch (e) {
        console.log(e);
      } finally {
        this.gridLoading = false;
      }
    },
    resetCompany() {
      this.recipientsModel.company.Email_FK = null;
    },
    resetContact() {
      this.recipientsModel.contact.Contact_FK = null;
      this.recipientsModel.contact.Email_FK = null;
    },
    async addRecipient({Company_FK, Contact_FK, Email_FK}) {
      if (!this.model.id) {
        return;
      }

      const getReferenceId = (obj) => {
        if (!obj) return null;

        return obj.id;
      };

      this.gridLoading = true;
      const recipient = {
        Company_FK: getReferenceId(Company_FK),
        Contact_FK: getReferenceId(Contact_FK),
        Email_FK: getReferenceId(Email_FK),
      };

      try {
        this.addingRecipient = true;
        await this.$api.facilities.distributionLists.update(this.model.id, {recipient});

        if (Contact_FK) {
          this.resetContact();
        } else {
          this.resetCompany();
        }

        this.notifySuccess('Recipient added');

        await this.refreshRecipients();
      } catch (e) {
        console.log(e);
        this.notifyError('Recipient failed to be added. Please try again.');
      } finally {
        this.gridLoading = false;
        this.addingRecipient = false;
      }
    },
    async removeRecipients() {
      const selected = this.gridApi.getSelectedNodes();

      if (selected.length === 0) {
        this.notifyError('Please select one or more recipients from the grid');
        return;
      }

      this.gridLoading = true;

      const recipientIds = selected.map(({data}) => data.id);

      try {
        await this.$api.facilities.distributionLists.removeRecipients(this.model.id, recipientIds);

        this.notifySuccess('Recipient removed');

        await this.refreshRecipients();
      } catch (e) {
        console.log(e);
        this.notifyError('Recipient failed to be removed. Please try again.');
      } finally {
        this.gridLoading = false;
      }
    }
  },
};
</script>

<style lang="scss">

</style>
